'use client';
import Loading from '@/app/(withLayout)/loading';
import { OnbForm } from '@/components/form';

import { sendOnbEvent } from '@/components/newRelic';
import { useStepData } from '@/components/stepData';
import { connectedField } from '@/redux/fields';
import { pushStepVariables } from '@/redux/flowSlice';
import { RootState } from '@/redux/store';
import { Box, FormControlLabel, Radio, RadioGroup, RadioGroupProps, Stack, TextField, Typography } from '@mui/material';
import { customerRegistration, getInstallmentData, repaymentTerms } from '@repo/onb-api';
import {
  COLORS,
  LOADING_STATUS_PROPS,
  LoadingStatusText,
  MaterialIcon,
  PlanOption,
  PlanOptionSkeleton,
} from '@repo/ui';
import { formatter, useDebounce } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from './stepWrapper';

export default function RepaymentTerms({
  stepname: stepName,
  stepdata: stepDataProp,
}: {
  stepname: repaymentTerms['name'];
  stepdata?: repaymentTerms['variables'];
}): React.JSX.Element {
  const stepData = useStepData(stepDataProp);
  const router = useRouter();
  const dispatch = useDispatch();
  const { id, variables = {} } = useSelector((state: RootState) => state.flowdata);
  const { firstName, policyMinimumAmount, policyMaximumAmount, requestedAmount } = variables;
  const [amount, setAmount] = useState(requestedAmount || policyMaximumAmount);
  const debouncedAmount = useDebounce<number>(amount || 0, 1500);
  const [amountError, setAmountError] = useState({ error: false, message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (stepData?.installments?.length === 0) {
      sendOnbEvent({ type: 'error', subtype: 'noInstallments', data: { stepName }, severity: 'high' });
      router.push('/error');
    }
  }, [stepData?.installments, stepName, router]);

  // Fewest installments first, as in customer-mobile; the plan with the most installments is the default
  const installments = useMemo(
    () => [...(stepData?.installments || [])].sort((a, b) => a.count - b.count),
    [stepData?.installments],
  );
  const defaultInstallmentValue =
    installments.find(({ planId }) => planId === stepData?.installmentPlanId)?.planId ??
    installments[installments.length - 1]?.planId;

  const handleAmountChange = async (value: number) => {
    setAmountError({ error: false, message: '' });
    setAmount(value);
    if ((policyMinimumAmount && value < policyMinimumAmount) || (policyMaximumAmount && value > policyMaximumAmount)) {
      setAmountError({
        error: true,
        message: `El monto solicitado debe estar entre ${formatter(policyMinimumAmount, 'currency')} y ${formatter(policyMaximumAmount, 'currency')}`,
      });
      // Every exit clears the loading state, or the plans stay hidden behind the spinner
      setIsLoading(false);
      return;
    }
    try {
      const installmentData = await getInstallmentData({
        instanceId: id,
        name: stepName,
        amount: value,
      });
      //@todo - not handling errors
      if (installmentData?.softError || installmentData?.hardError) {
        sendOnbEvent({
          type: 'error',
          subtype: 'getInstallmentData',
          data: { stepName, error: installmentData.softError || installmentData.hardError },
          severity: 'high',
        });
        setAmountError({ error: true, message: 'Hubo un error al cargar los planes de cuotas' });
        setIsLoading(false);
        return;
      }
      if (stepName && installmentData?.variables?.installments)
        dispatch(
          pushStepVariables({ stepName, variable: 'installments', value: installmentData.variables.installments }),
        );
    } catch (e) {
      setAmountError({
        error: true,
        message: `El monto solicitado debe estar entre ${formatter(policyMinimumAmount, 'currency')} y ${formatter(policyMaximumAmount, 'currency')}`,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    handleAmountChange(debouncedAmount);
  }, [debouncedAmount]);

  // connectedField runs hooks: call it before the early Loading return, never inside a conditional branch
  const amountField = connectedField({
    onChange: (e: any) => {
      //@todo - narrow down types
      const numericValue = Number(e.target.value.replace(/[^0-9]/g, ''));
      setIsLoading(true);
      setAmount(numericValue);
    },
    stepName,
    name: 'requestedAmount',
    defaultValue: policyMaximumAmount,
    beforeChange: (value) => (typeof value === 'string' ? Number(value?.replace(/[^0-9]/g, '')) : value),
  });
  const planField = connectedField({
    defaultValue: defaultInstallmentValue,
    stepName,
    name: 'installmentPlanId',
    beforeRender: (value) => (value ? String(value) : ''),
    beforeChange: (value) => Number(value),
  });
  const planValue = 'value' in planField ? planField.value : '';
  const selectedPlan = installments.find(({ planId }) => String(planId) === planValue);

  // New amount, new plans: when the picked plan is gone, fall back to the default one
  useEffect(() => {
    if (stepName && installments.length && !selectedPlan && defaultInstallmentValue)
      dispatch(pushStepVariables({ stepName, variable: 'installmentPlanId', value: defaultInstallmentValue }));
  }, [stepName, installments, selectedPlan, defaultInstallmentValue, dispatch]);

  // Only the ceiling is shown; the minimum is still enforced by the amount validation
  const amountRange = policyMaximumAmount
    ? `Podés pedir hasta ${formatter(policyMaximumAmount, 'currency')}`
    : undefined;
  const rates = selectedPlan?.rates;

  return (stepData?.installments?.length || 0) === 0 ? (
    <Loading />
  ) : (
    <StepWrapper>
      <Typography variant="h1">
        ¡Buenas noticias{firstName ? ` ${formatter(firstName, 'capitalize')}!` : '!'}
      </Typography>
      <Typography component="p" variant="subtitle2">
        Esta es tu oferta. Podés cambiar el monto y elegir en cuántas cuotas pagás.
      </Typography>
      <OnbForm<customerRegistration> stepName={stepName!} disabled={isLoading || amountError.error || !selectedPlan}>
        <Box
          // Room around the hero amount, as in customer-mobile's AmountEntry
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 2,
          }}
        >
          <TextField
            label="Monto que estás solicitando"
            data-testid="requestedAmount"
            sx={amountFieldSx(formatter(amount?.toString(), 'currency').length)}
            {...amountField}
            error={amountError?.error}
            helperText={amountError?.error ? amountError.message : amountRange}
            value={formatter(amount?.toString(), 'currency')}
          />
        </Box>
        <Stack sx={{ mt: 3, mb: 2, gap: 2 }}>
          <Typography id="installmentPlanId-label" component="h2" variant="h2" sx={{ color: COLORS.text.main }}>
            Elegí en cuántas cuotas pagás
          </Typography>
          {isLoading ? (
            // The plans are recalculated for the new amount
            <Stack {...LOADING_STATUS_PROPS}>
              {Array.from({ length: installments.length || 3 }, (_, index) => (
                <PlanOptionSkeleton key={index} />
              ))}
              <LoadingStatusText label="Calculando cuotas" />
            </Stack>
          ) : amountError.error ? (
            // The plans listed belong to the last valid amount: hide them until the amount is fixed
            <Typography variant="subtitle2" sx={{ textAlign: 'center', py: 2 }}>
              Ingresá un monto válido para ver las cuotas disponibles.
            </Typography>
          ) : (
            <>
              <RadioGroup
                aria-labelledby="installmentPlanId-label"
                data-testid="installmentPlanId"
                name="installmentPlanId"
                value={planValue}
                onChange={(planField as Pick<RadioGroupProps, 'onChange'>).onChange}
                sx={{ '& > label:nth-last-of-type(1)': { margin: 0 } }}
              >
                {installments.map((plan) => (
                  <FormControlLabel
                    key={plan.planId}
                    value={String(plan.planId)}
                    control={<Radio />}
                    label={<PlanOption {...plan} />}
                    sx={{ py: 1.5, '& .MuiFormControlLabel-label': { flex: 1 } }}
                  />
                ))}
              </RadioGroup>
              {selectedPlan && (
                // The badge rings spread 4px past the icon, so the gaps leave 16px of clear space as in customer-mobile
                <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5, mt: 1, pl: 0.5 }}>
                  <MaterialIcon icon="receipt_long" color="primary" containerType="circle" aria-hidden="true" />
                  <Stack sx={{ gap: 0.25 }}>
                    <Typography variant="subtitle2">
                      Pagás tu primera cuota el {formatter(selectedPlan.firstDueDate, 'date')}
                    </Typography>
                    {rates && (
                      <Typography variant="subtitle2" sx={{ color: COLORS.text.main }}>
                        <b>TEA</b> {formatter(rates.TEA, 'number', 2)}% · <b>TNA</b> {formatter(rates.TNA, 'number', 2)}
                        % · <b>CFTEA</b> {formatter(rates.CFTTEA, 'number', 2)}%
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Stack>
      </OnbForm>
    </StepWrapper>
  );
}

const AMOUNT_FONT_SIZE = 56;

// Big centered amount over a purple underline, like customer-mobile's AmountEntry; long amounts shrink to one line
const amountFieldSx = (length: number) => ({
  '& .MuiInputLabel-root': { alignSelf: 'center' },
  '& .MuiInputBase-root': {
    height: 'auto',
    backgroundColor: 'transparent',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 4,
      borderRadius: 2,
      backgroundColor: COLORS.brand.light,
    },
    '&.Mui-error::after': { backgroundColor: COLORS.state.error },
  },
  '& .MuiOutlinedInput-notchedOutline': { display: 'none' },
  '& .MuiInputBase-input': {
    padding: '0 0 8px',
    minHeight: 68,
    boxSizing: 'border-box',
    textAlign: 'center',
    fontWeight: 700,
    fontSize: AMOUNT_FONT_SIZE * Math.min(1, 8 / Math.max(length, 1)),
    letterSpacing: '-1px',
    color: COLORS.primary.blue.main,
    caretColor: COLORS.brand.main,
  },
  '& .MuiFormHelperText-root': { textAlign: 'center' },
});
